/**
 * Browser-side brand access.
 *
 * There is no bootstrap any more, and that is the point. The brand is baked
 * into `index.html` at build time by `apps/web/vite-plugin-brand.ts`, so by
 * the time any module here runs the value is already on `window` — and, more
 * importantly, was already in the markup of the first painted frame.
 *
 * What this replaced: a fetch of `/api/public/brand` kicked off before
 * `createRoot`, a subscribable store so React could re-render when the
 * response landed, and a hostname-keyed `localStorage` cache read by an inline
 * script in `<head>` to hide the resulting flash on repeat visits. All three
 * existed to paper over a value that arrived after first paint. None is needed
 * for a value that ships inside the HTML.
 *
 * The snapshot is therefore CONSTANT for the life of the document. Nothing
 * subscribes, nothing invalidates, and a brand change is a rebuild — which is
 * the deployment model: brands are vendor-configured, not self-service.
 */
import { DEFAULT_BRAND } from './default-brand.js'
import type { BrandSnapshot, BrandLanding } from './snapshot.js'

// Re-exported because `getBrandLanding()` returns it: a browser caller that
// wants to NAME the type (a prop, a lookup keyed by landing) would otherwise
// have to reach past this entry point into `@papan/brand`, which pulls the
// server/config surface in with it.
export type { BrandLanding }

/**
 * The global the build plugin writes. Must match `BRAND_GLOBAL` in
 * `apps/web/vite-plugin-brand.ts`; `brand-injection.test.ts` pins the pair,
 * because a rename on one side degrades silently to the default brand — the
 * page renders, it just renders as the vendor.
 */
export const BRAND_GLOBAL = '__PAPAN_BRAND__'

/**
 * Resolved once, at module load.
 *
 * Falls back to `DEFAULT_BRAND` when the global is absent, which covers the
 * two legitimate cases: a unit test rendering a component in isolation, and
 * any HTML entry point that does not go through the Vite transform. It does
 * NOT validate the injected value — it was produced by our own build from a
 * file we authored, and a runtime schema check here could only turn a
 * build-time mistake into a blank screen later.
 */
const SNAPSHOT: BrandSnapshot = readInjectedBrand() ?? DEFAULT_BRAND

function readInjectedBrand(): BrandSnapshot | null {
  if (typeof window === 'undefined') return null
  const raw = (window as unknown as Record<string, unknown>)[BRAND_GLOBAL]
  if (!raw || typeof raw !== 'object') return null
  return raw as BrandSnapshot
}

/** The brand for this deployment. Constant for the life of the document. */
export function getBrandSnapshot(): BrandSnapshot {
  return SNAPSHOT
}

/**
 * Which surface this deployment's BUILD says is home.
 *
 * ⚠ Not the final answer since 2026-08-07: `platform_settings.landing_experience`
 * overrides it at runtime. The SPA's one consumer is
 * `apps/web/src/lib/landing.ts`, which uses this as the synchronous SEED and
 * then corrects it from `GET /api/public/experience` behind the boot skeleton.
 * Anything that needs "where does this deployment actually land" must go
 * through that module's `landingView()` / `landingPath()`, never through here.
 *
 * Read through a function, and defensively, for one reason the type does not
 * show: `experience` arrived in schema v3, and the injected global comes from
 * an `index.html` the browser may have CACHED from a v2 build. The type says
 * the field is required — of a snapshot our current build produced — but the
 * value on `window` can predate it, and `readInjectedBrand` deliberately does
 * not validate. A bare `SNAPSHOT.experience.landing` is therefore a TypeError
 * on a stale document: a white screen for a returning visitor, from a field
 * they never set.
 *
 * The value is re-checked against the union for the same reason the loader
 * checks it — this decides a redirect target, and an unrecognised one bounces
 * through the catch-all forever.
 */
export function getBrandLanding(): BrandLanding {
  const landing = SNAPSHOT.experience?.landing
  return landing === 'chat' || landing === 'launcher' ? landing : DEFAULT_BRAND.experience.landing
}
